<script setup>
import { computed, ref, shallowRef } from 'vue';
import { useStore } from 'vuex';
import SettingsSection from '@/components/settings/SettingsSection.vue';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiFormDialog from '@/components/ui/UiFormDialog.vue';
import UiFormField from '@/components/ui/UiFormField.vue';
import { apiRequest } from '@/api/http';
import { useBranding } from '@/branding';
import { useAsyncAction } from '@/composables/useAsyncAction';
import { useBrandAssets } from '@/composables/useBrandAssets';
import { useConfirm } from '@/composables/useConfirm';
import { RoleCode } from '@/constants/accounts';
import { formatDate } from '@/utils/format';

// The public landing page: its hero photo, and the school announcements
// shown there. The photo applies as soon as it's uploaded; announcements
// save one at a time and only the superadmin can post them.
const PHOTO = 'landing-hero';

const branding = useBranding();
const { busy: photoBusy, upload, reset: removeAsset } = useBrandAssets();
const photoUrl = computed(() => {
  const hero = branding.assets[PHOTO];
  return hero?.custom ? hero.url : '/img/landingpage.jpg';
});
const customPhoto = computed(() => !!branding.assets[PHOTO]?.custom);

let input = null;
function onFile(event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  upload(PHOTO, file);
}

// --- Announcements ------------------------------------------------------
const store = useStore();
const isSuperadmin = computed(() => store.getters.activeRoleCode === RoleCode.Superadmin);
const { confirm } = useConfirm();
const { busy, run } = useAsyncAction();

const announcements = shallowRef([]);
const loading = shallowRef(false);
async function load() {
  loading.value = true;
  try {
    const rows = await apiRequest('get', '/school-announcements');
    announcements.value = Array.isArray(rows) ? rows : [];
  } catch {
    announcements.value = [];
  } finally {
    loading.value = false;
  }
}
load();

const FIELDS = [
  { key: 'title', label: 'Title', required: true, placeholder: 'e.g. Enrolment for SY 2026–2027 is open' },
  { key: 'body', label: 'Message', type: 'textarea', required: true },
];
const editing = shallowRef(null);
const formOpen = shallowRef(false);
const form = ref({ title: '', body: '' });
const pinned = shallowRef(false);

function openForm(a = null) {
  editing.value = a;
  form.value = { title: a?.title ?? '', body: a?.body ?? '' };
  pinned.value = !!a?.pinned;
  formOpen.value = true;
}

async function save() {
  const body = { title: form.value.title, body: form.value.body, pinned: pinned.value };
  const id = editing.value?.id;
  const ok = await run(
    () => (id ? apiRequest('patch', `/school-announcements/${id}`, body) : apiRequest('post', '/school-announcements', body)),
    { success: id ? 'Announcement updated.' : 'Announcement posted.', error: 'Could not save the announcement.' },
  );
  if (ok) {
    formOpen.value = false;
    load();
  }
}

async function remove(a) {
  const ok = await confirm({
    title: 'Delete announcement?',
    description: `"${a.title}" will be removed from the landing page.`,
    confirmText: 'Delete',
    destructive: true,
  });
  if (ok && (await run(() => apiRequest('delete', `/school-announcements/${a.id}`), { success: 'Announcement deleted.', error: 'Could not delete the announcement.' }))) {
    load();
  }
}
</script>

<template>
  <SettingsSection
    title="Landing page"
    description="The public home page people see before signing in."
    hide-actions
  >
    <div class="form-stack">
      <UiFormField
        label="Photo"
        description="Shown beside the school name at the top. A landscape photo of the campus works best. Applies as soon as you upload."
      >
        <div class="photo">
          <div class="photo__thumb">
            <img :src="photoUrl" alt="Landing page photo" />
          </div>
          <div class="photo__actions">
            <v-btn
              variant="outlined"
              size="small"
              prepend-icon="mdi-upload-outline"
              :loading="photoBusy === PHOTO"
              @click="input?.click()"
            >
              {{ customPhoto ? 'Replace' : 'Upload' }}
            </v-btn>
            <v-btn
              v-if="customPhoto"
              variant="text"
              size="small"
              :disabled="photoBusy === PHOTO"
              @click="removeAsset(PHOTO, { optional: true })"
            >
              Use the default
            </v-btn>
            <input
              :ref="(el) => (input = el)"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              hidden
              @change="onFile"
            />
          </div>
        </div>
      </UiFormField>

      <UiFormField
        label="Announcements"
        :description="isSuperadmin
          ? 'Posted on the landing page for everyone. Pinned ones are listed first.'
          : 'Posted on the landing page for everyone. Only the superadmin can post or change them.'"
      >
        <div class="notices">
          <div v-if="loading && !announcements.length" class="notices__empty">Loading…</div>
          <div v-else-if="!announcements.length" class="notices__empty">No announcements on the landing page yet.</div>
          <div v-for="a in announcements" :key="a.id" class="notice">
            <div class="notice__text">
              <div class="notice__head">
                <span class="notice__title">{{ a.title }}</span>
                <UiBadge v-if="a.pinned" variant="blue">Pinned</UiBadge>
              </div>
              <span class="notice__meta">{{ formatDate(a.createdAt, 'MMM D, YYYY') }} · {{ a.body }}</span>
            </div>
            <div v-if="isSuperadmin" class="notice__actions">
              <v-btn size="small" variant="text" @click="openForm(a)">Edit</v-btn>
              <v-btn size="small" variant="text" icon="mdi-trash-can-outline" :aria-label="`Delete ${a.title}`" @click="remove(a)" />
            </div>
          </div>
          <div v-if="isSuperadmin">
            <v-btn variant="outlined" size="small" prepend-icon="mdi-plus" @click="openForm()">New announcement</v-btn>
          </div>
        </div>
      </UiFormField>
    </div>

    <UiFormDialog
      v-model="formOpen"
      v-model:values="form"
      :title="editing ? 'Edit announcement' : 'New announcement'"
      description="Shown on the landing page for everyone."
      :fields="FIELDS"
      size="md"
      :saving="busy"
      :submit-text="editing ? 'Save changes' : 'Post'"
      @submit="save"
    >
      <v-switch
        v-model="pinned"
        color="primary"
        label="Pin to the top"
        inset
        density="compact"
        hide-details
      />
    </UiFormDialog>
  </SettingsSection>
</template>

<style scoped>
.photo {
  display: flex;
  align-items: center;
  gap: 12px;
}
.photo__thumb {
  display: flex;
  flex: 0 0 128px;
  align-items: center;
  justify-content: center;
  height: 80px;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--subtle);
}
.photo__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.photo__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.notices {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.notices__empty {
  padding: 12px;
  font-size: 13px;
  color: var(--muted-foreground);
  border: 1px dashed var(--border-strong);
  border-radius: 8px;
}
.notice {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
}
.notice__text {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}
.notice__head {
  display: flex;
  align-items: center;
  gap: 8px;
}
.notice__title {
  font-size: 14px;
  font-weight: 500;
}
.notice__meta {
  overflow: hidden;
  font-size: 13px;
  color: var(--muted-foreground);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.notice__actions {
  display: flex;
  flex-shrink: 0;
}
</style>
